// "Get a design review" — a file (or a Symphony-built site) handed to the agent, which asks Wixel's designer
// through its DesignReview tool. No endpoint of its own: the action opens a chat (the same plumbing Discuss
// uses) and SENDS a request message; the agent does the rest.
//
// ONE eligibility rule on all three surfaces (iOS `DesignReview.swift`, Android `DesignReview.kt`):
//   - a file whose URL is a Wixel short link (`isWixelShortLink`, the same parser Edit-in-Wixel uses), OR
//   - a file an AGENT wrote — writer MAESTRO / SUB_AGENT / PULSE, origin CONVERSATION / AGENT_TASK — that is an
//     image, a PDF or a video by the doc-viewer kind classifier.
// Never a memory, a transcript or a life-timeline row, and never a USER or CONNECTED_AGENT write, whatever its
// URL (the same exclusions iOS and Android apply). A file with no recorded writer/origin (a chat attachment) is
// eligible only through the Wixel-link half.

import { extractUrl } from './artifact-url';
import type { ChatHandoffNew } from './chat-handoff';
import type { DocViewerKind } from './doc-viewer';
import { filePath, isLifeTimelinePath, isMemoryPath, isTranscriptsPath } from './file-browser';
import { effectiveContent } from './file-record';
import { isWixelShortLink } from './wixel-link';
import type { WorkspaceFile } from './workspace-files';

/** The menu label, on the file menu and the site strip alike. */
export const DESIGN_REVIEW_ACTION_KEY = 'designReview.action';
/** "Get a design review of {name} from Wixel's designer." */
export const DESIGN_REVIEW_FILE_REQUEST_KEY = 'designReview.request.file';
/** "Get a design review of my website {name} from Wixel's designer." */
export const DESIGN_REVIEW_SITE_REQUEST_KEY = 'designReview.request.site';

const AGENT_WRITERS: ReadonlySet<string> = new Set(['MAESTRO', 'SUB_AGENT', 'PULSE']);
const NEVER_WRITERS: ReadonlySet<string> = new Set(['USER', 'CONNECTED_AGENT']);
const AGENT_ORIGINS: ReadonlySet<string> = new Set(['CONVERSATION', 'AGENT_TASK']);
const REVIEWABLE_KINDS: ReadonlySet<DocViewerKind> = new Set<DocViewerKind>(['image', 'pdf', 'video']);

/** Whether "Get a design review" is offered for this file. See the module header for the rule. */
export function isDesignReviewEligible(
  item: WorkspaceFile,
  kind: DocViewerKind | null,
): boolean {
  const path = filePath(item);
  if (isMemoryPath(path) || isTranscriptsPath(path) || isLifeTimelinePath(path)) return false;
  if (item.writerType && NEVER_WRITERS.has(item.writerType)) return false;
  if (isWixelShortLink(extractUrl(effectiveContent(item)))) return true;
  return (
    !!item.writerType &&
    AGENT_WRITERS.has(item.writerType) &&
    !!item.originType &&
    AGENT_ORIGINS.has(item.originType) &&
    kind !== null &&
    REVIEWABLE_KINDS.has(kind)
  );
}

type Translate = (key: string, options?: Record<string, unknown>) => string;

/** The message the file action SENDS. */
export function designReviewFileRequest(t: Translate, name: string): string {
  return t(DESIGN_REVIEW_FILE_REQUEST_KEY, { name });
}

/** The Space a file belongs to, when it has one: the account-wide read stamps `workspaceId`, a Space-scoped read
 *  leaves it off and the file's `vfsPath` (`workspaces/<id>/…`) still names it. Undefined for an account file (a
 *  skill, an agent's note) and a chat attachment — those open the general new chat. */
export function designReviewSpaceId(item: Pick<WorkspaceFile, 'workspaceId' | 'vfsPath'>): string | undefined {
  if (item.workspaceId) return item.workspaceId;
  const match = /^workspaces\/([^/]+)\//.exec(item.vfsPath ?? '');
  return match ? match[1] : undefined;
}

/** The chat handoff the file action opens: a NEW conversation (in the file's Space when it has one) that sends
 *  the request on arrival — the same `kind: 'new'` handoff Discuss uses, with the design-review message. */
export function designReviewHandoff(item: WorkspaceFile, t: Translate): ChatHandoffNew {
  const workspaceId = designReviewSpaceId(item);
  return { kind: 'new', message: designReviewFileRequest(t, item.name), ...(workspaceId ? { workspaceId } : {}) };
}
